import React from 'react';
import { PhoneMissed, PhoneOff, PhoneOutgoing, PhoneCall } from 'lucide-react';

/**
 * System-message chip rendered inline in a chat thread for every
 * voice-call outcome — missed, no_answer, declined, and completed.
 * Backed by the `call_event` messages the backend writes from
 * `/calls/{id}/end` and `/calls/{id}/decline` (see
 * `routes/call_routes.py`) — this component only handles rendering,
 * not persistence. Copy adapts to whether the viewer is the caller
 * or the callee (derived from `message.sender_id === viewerId`),
 * and completed calls also show the duration.
 *
 * Layout: centered pill; danger-tinted for missed/declined,
 * neutral-tinted for completed. Icon left, copy middle, "Call back"
 * primary text-button right. Tap-and-hold and reactions are
 * deliberately NOT wired — this is a passive system message, not
 * a user message.
 */
const formatChipTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(parsed);
};

const formatDuration = (seconds) => {
  const total = Math.max(0, parseInt(seconds ?? 0, 10) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const resolveCopy = (status, isMine, metadata) => {
  if (status === 'completed') {
    return {
      icon: PhoneCall,
      title: 'Call ended',
      subtitle: formatDuration(metadata?.duration_seconds),
      variant: 'neutral',
    };
  }
  if (status === 'declined') {
    return {
      icon: PhoneOff,
      title: isMine ? 'You declined the call' : 'Call declined',
      variant: 'danger',
    };
  }
  // 'no_answer' or 'missed'. When the caller (the one who fired
  // end-with-no_answer) sees this bubble it's their own event, so
  // "No answer". The callee sees it as "You missed a call".
  return {
    icon: isMine ? PhoneOutgoing : PhoneMissed,
    title: isMine ? 'No answer' : 'You missed a call',
    variant: 'danger',
  };
};

const VARIANT_STYLES = {
  danger: {
    background: 'var(--status-danger-surface, rgba(233, 76, 87, 0.10))',
    border: '1px solid var(--accent-border-soft, rgba(233, 76, 87, 0.22))',
    iconBg: 'var(--status-danger-icon, rgba(233, 76, 87, 0.18))',
    iconColor: 'var(--status-danger-text, #C4373F)',
    textColor: 'var(--status-danger-text, #C4373F)',
  },
  neutral: {
    background: 'var(--surface-2, rgba(0, 0, 0, 0.04))',
    border: '1px solid var(--border-soft, rgba(0, 0, 0, 0.08))',
    iconBg: 'var(--surface-3, rgba(0, 0, 0, 0.06))',
    iconColor: 'var(--text-1, #1f1f1f)',
    textColor: 'var(--text-1, #1f1f1f)',
  },
};

export const MissedCallChip = ({
  message,
  viewerId,
  onCallBack,
  canCallBack = true,
  dataTestId,
}) => {
  const status = (message?.metadata || {}).status || 'missed';
  const isMine = viewerId && message?.sender_id === viewerId;
  const { icon: Icon, title, subtitle, variant } = resolveCopy(status, isMine, message?.metadata);
  const secondaryLabel = subtitle || formatChipTime(message?.created_at);
  const palette = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  return (
    <div
      data-testid={dataTestId}
      style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '10px 0',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 14px 8px 12px',
          borderRadius: 999,
          background: palette.background,
          border: palette.border,
          boxShadow: 'var(--premium-shadow-nested, 0 6px 14px -8px rgba(0,0,0,0.14))',
          maxWidth: '92%',
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: palette.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={14} strokeWidth={2.4} color={palette.iconColor} />
        </div>
        <span
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 13.5,
            fontWeight: 600,
            color: palette.textColor,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {secondaryLabel ? (
          <span
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 11.5,
              color: 'var(--text-2)',
              letterSpacing: '0.02em',
            }}
          >
            · {secondaryLabel}
          </span>
        ) : null}
        {canCallBack && onCallBack ? (
          <button
            type="button"
            data-testid={dataTestId ? `${dataTestId}-callback-btn` : undefined}
            onClick={onCallBack}
            style={{
              marginLeft: 4,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--accent-purple)',
              color: '#FFFFFF',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'Inria Serif', serif",
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: '0.02em',
              boxShadow: '0 6px 14px -8px rgba(112, 63, 246, 0.55)',
            }}
          >
            Call back
          </button>
        ) : null}
      </div>
    </div>
  );
};
